import { Controller } from '@hotwired/stimulus'

const STAGE_TITLES = ['Choose background', 'Arrange strip images', 'Add shapes', 'Preview']
const MAX_PAGES = 10
const EFFECT_KEYS = ['wow', 'omg', 'pow', 'cool', 'boom', 'ok', 'lol', 'bang', 'oh', 'no', 'why', 'pop', 'yeah', 'hello', 'bam', 'zap']
const RESIZE_DIRECTIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
export default class extends Controller {
  static targets = [
    'projectData', 'projectName', 'status', 'backgroundStage', 'composeStage', 'previewStage',
    'shapeTray', 'shapeMenuButton', 'stageTitle', 'backButton', 'nextButton', 'canvasShell', 'canvasLayer',
    'emptyState', 'objectToolbar', 'previewCanvas', 'pageButtons', 'assetList', 'rackSelect',
    'exportButton', 'editTextButton', 'addPageButton', 'deletePageButton'
  ]

  connect () {
    const payload = JSON.parse(this.projectDataTarget.textContent)
    this.projectId = payload.id
    this.updateUrl = payload.update_url
    this.exportUrl = payload.export_url
    this.effectSources = new Map(
      [...this.element.querySelectorAll('[data-effect-key]')].map(button => [button.dataset.effectKey, button.dataset.effectSrc])
    )
    this.pages = (payload.pages || []).map(page => this.normalizePage(page))
    if (this.pages.length === 0) this.pages = [this.blankPage()]
    this.pageIndex = 0
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.selectedId = null
    this.hasUnsavedChanges = false
    this.backgroundSources = new Map(
      [...this.element.querySelectorAll('[data-background-key]')].map(button => [button.dataset.backgroundKey, button.dataset.backgroundSrc])
    )
    this.keydownHandler = event => this.handleKeydown(event)
    this.beforeUnloadHandler = event => this.warnBeforeUnload(event)
    this.beforeVisitHandler = event => this.warnBeforeVisit(event)
    document.addEventListener('keydown', this.keydownHandler)
    globalThis.addEventListener('beforeunload', this.beforeUnloadHandler)
    document.addEventListener('turbo:before-visit', this.beforeVisitHandler)
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
  }

  disconnect () {
    document.removeEventListener('keydown', this.keydownHandler)
    globalThis.removeEventListener('beforeunload', this.beforeUnloadHandler)
    document.removeEventListener('turbo:before-visit', this.beforeVisitHandler)
    this.abortPointerInteraction?.()
    this.abortAssetPointerDrag?.()
  }

  blankPage () {
    return { id: null, backgroundKey: null, objects: [] }
  }

  normalizePage (page) {
    const data = page.editor_data || {}
    return {
      id: page.id || null,
      backgroundKey: page.background_key || null,
      objects: Array.isArray(data.objects) ? data.objects.map((object, index) => this.normalizeObject(object, index)) : []
    }
  }

  normalizeObject (object, index = 0) {
    return {
      localId: object.localId || object.local_id || this.localId(),
      type: ['image', 'bubble', 'text', 'sticker'].includes(object.type) ? object.type : 'image',
      assetId: object.asset_id || object.assetId || null,
      sourceAttachmentId: object.source_attachment_id || object.sourceAttachmentId || null,
      src: this.staticEffectSourceFor(object) || object.src || null,
      text: String(object.text || '').slice(0, 500),
      x: this.number(object.x, 12),
      y: this.number(object.y, 12),
      width: this.number(object.width, 42),
      height: this.number(object.height, 42),
      rotation: this.number(object.rotation, 0),
      z: this.number(object.z, index + 1)
    }
  }

  staticEffectSourceFor (object) {
    if (object.type !== 'sticker') return null

    const explicitKey = String(object.effect_key || object.effectKey || '').toLowerCase()
    const source = String(object.src || '')
    const effectKey = EFFECT_KEYS.includes(explicitKey)
      ? explicitKey
      : EFFECT_KEYS.find(key => new RegExp(`(?:^|/)${key}(?:-[a-z0-9]+)?\\.png(?:[?#]|$)`, 'i').test(source))
    return this.effectSources?.get(effectKey) || null
  }

  localId () {
    return globalThis.crypto?.randomUUID?.() || `comic-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  number (value, fallback) {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
  }

  currentPage () { return this.pages[this.pageIndex] }

  chooseBackground (event) {
    const page = this.currentPage()
    page.backgroundKey = event.currentTarget.dataset.backgroundKey
    this.element.querySelectorAll('[data-background-key]').forEach(button => {
      button.classList.toggle('selected', button.dataset.backgroundKey === page.backgroundKey)
    })
    this.renderCanvasBackground()
    this.emptyStateTarget.hidden = true
    this.queueSave()
  }

  async nextStage () {
    if (this.stage === 0 && !this.currentPage().backgroundKey) {
      this.setStatus('Choose a background before continuing.', true)
      return
    }
    if (this.stage === 2) {
      this.nextButtonTarget.disabled = true
      const saved = await this.saveProject()
      this.nextButtonTarget.disabled = false
      if (!saved) return
    }
    if (this.stage < 3) this.stage += 1
    this.renderStage()
    if (this.stage === 3) this.renderPreview()
  }

  previousStage () {
    if (this.stage > 0) this.stage -= 1
    this.renderStage()
  }

  editCurrentPage () {
    this.stage = 2
    this.renderStage()
  }

  renderStage () {
    this.stageTitleTarget.textContent = STAGE_TITLES[this.stage]
    this.backgroundStageTarget.classList.toggle('d-none', this.stage !== 0)
    this.composeStageTarget.classList.toggle('d-none', ![1, 2].includes(this.stage))
    this.shapeMenuButtonTarget.classList.toggle('d-none', this.stage !== 2)
    if (this.stage !== 2) this.closeShapeMenu()
    this.previewStageTarget.classList.toggle('d-none', this.stage !== 3)
    this.backButtonTarget.classList.toggle('d-none', this.stage === 0)
    this.nextButtonTarget.classList.toggle('d-none', this.stage === 3)
    this.nextButtonTarget.textContent = this.stage === 2 ? 'Done' : 'Next'
    if ([1, 2].includes(this.stage)) this.renderPage()
  }

  addAsset (event) {
    if (this.ignoreNextAssetClick) return
    const button = event.currentTarget
    this.addObject({
      type: 'image',
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc,
      text: '',
      x: 18,
      y: 18,
      width: 48,
      height: 48
    })
  }

  beginAssetPointerDrag (event) {
    if (event.button !== 0) return
    event.preventDefault()
    const button = event.currentTarget
    const start = { x: event.clientX, y: event.clientY }
    let moved = false
    this.draggedAsset = {
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc
    }

    const move = pointer => {
      if (!moved && Math.hypot(pointer.clientX - start.x, pointer.clientY - start.y) < 6) return
      moved = true
      button.classList.add('dragging')
      const rect = this.canvasLayerTarget.getBoundingClientRect()
      const overCanvas = this.currentPage().backgroundKey && this.pointInsideRect(pointer.clientX, pointer.clientY, rect)
      this.canvasLayerTarget.classList.toggle('asset-drop-active', Boolean(overCanvas))
    }
    const finish = pointer => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', finish)
      document.removeEventListener('pointercancel', cancel)
      button.classList.remove('dragging')
      this.canvasLayerTarget.classList.remove('asset-drop-active')
      if (moved) {
        this.ignoreNextAssetClick = true
        this.placeDraggedAsset(pointer.clientX, pointer.clientY)
        setTimeout(() => { this.ignoreNextAssetClick = false }, 0)
      }
      this.draggedAsset = null
      this.abortAssetPointerDrag = null
    }
    const cancel = () => finish({ clientX: -1, clientY: -1 })
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', finish, { once: true })
    document.addEventListener('pointercancel', cancel, { once: true })
    this.abortAssetPointerDrag = cancel
  }

  startAssetDrag (event) {
    const button = event.currentTarget
    this.draggedAsset = {
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc
    }
    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.setData('text/plain', String(this.draggedAsset.assetId))
    button.classList.add('dragging')
  }

  endAssetDrag (event) {
    event.currentTarget.classList.remove('dragging')
    this.canvasLayerTarget.classList.remove('asset-drop-active')
    this.draggedAsset = null
  }

  showAssetDropTarget (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    this.canvasLayerTarget.classList.add('asset-drop-active')
  }

  allowAssetDrop (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  hideAssetDropTarget (event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    this.canvasLayerTarget.classList.remove('asset-drop-active')
  }

  dropAsset (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    this.placeDraggedAsset(event.clientX, event.clientY)
    this.canvasLayerTarget.classList.remove('asset-drop-active')
  }

  placeDraggedAsset (clientX, clientY) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return false
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    if (!this.pointInsideRect(clientX, clientY, rect)) return false
    const width = 42
    const height = 42
    const x = Math.max(0, Math.min(100 - width, (clientX - rect.left) / rect.width * 100 - width / 2))
    const y = Math.max(0, Math.min(100 - height, (clientY - rect.top) / rect.height * 100 - height / 2))
    this.addObject({
      type: 'image',
      assetId: this.draggedAsset.assetId,
      sourceAttachmentId: this.draggedAsset.sourceAttachmentId,
      src: this.draggedAsset.src,
      text: '',
      x,
      y,
      width,
      height
    })
    return true
  }

  pointInsideRect (x, y, rect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  addShape (event) {
    const object = this.addObject({
      type: 'bubble',
      src: event.currentTarget.dataset.shapeSrc,
      text: '',
      x: 22,
      y: 16,
      width: 45,
      height: 24
    })
    this.closeShapeMenu()
    if (object) this.editObjectText(object)
  }

  addText () {
    const object = this.addObject({
      type: 'text',
      text: '',
      x: 24,
      y: 35,
      width: 50,
      height: 14
    })
    this.closeShapeMenu()
    if (object) this.editObjectText(object)
  }

  addEffect (event) {
    this.addObject({
      type: 'sticker',
      src: event.currentTarget.dataset.effectSrc,
      text: '',
      x: 28,
      y: 28,
      width: 42,
      height: 24
    })
    this.closeShapeMenu()
  }

  toggleShapeMenu () {
    const opening = this.shapeTrayTarget.classList.contains('d-none')
    this.shapeTrayTarget.classList.toggle('d-none', !opening)
    this.shapeMenuButtonTarget.setAttribute('aria-expanded', String(opening))
  }

  closeShapeMenu () {
    this.shapeTrayTarget.classList.add('d-none')
    this.shapeMenuButtonTarget.setAttribute('aria-expanded', 'false')
  }

  addObject (attributes) {
    if (!this.currentPage().backgroundKey) {
      this.stage = 0
      this.renderStage()
      return this.setStatus('Choose a background first.', true)
    }
    const page = this.currentPage()
    if (page.objects.length >= 100) return this.setStatus('A page can contain up to 100 objects.', true)

    const object = this.normalizeObject({ ...attributes, localId: this.localId(), z: page.objects.length + 1 })
    page.objects.push(object)
    if (this.stage === 0 || this.stage === 3) this.stage = ['bubble', 'text', 'sticker'].includes(attributes.type) ? 2 : 1
    this.renderStage()
    this.selectObject(object.localId)
    this.queueSave()
    return object
  }

  renderPage () {
    const page = this.currentPage()
    this.canvasLayerTarget.innerHTML = ''
    this.renderCanvasBackground()
    page.objects.sort((a, b) => a.z - b.z).forEach(object => this.canvasLayerTarget.appendChild(this.objectElement(object)))
    this.emptyStateTarget.hidden = Boolean(page.backgroundKey)
    this.selectedId = null
    this.objectToolbarTarget.hidden = true
    this.highlightSelectedBackground()
  }

  renderCanvasBackground () {
    const source = this.backgroundSources.get(this.currentPage().backgroundKey)
    this.canvasShellTarget.style.backgroundImage = source ? `url("${source}")` : 'none'
  }

  highlightSelectedBackground () {
    const selected = this.currentPage().backgroundKey
    this.element.querySelectorAll('[data-background-key]').forEach(button => button.classList.toggle('selected', button.dataset.backgroundKey === selected))
  }

  objectElement (object) {
    const element = document.createElement('div')
    element.className = `comic-canvas-object comic-object-${object.type}`
    element.dataset.objectId = object.localId
    element.style.left = `${object.x}%`
    element.style.top = `${object.y}%`
    element.style.width = `${object.width}%`
    element.style.height = `${object.height}%`
    element.style.zIndex = object.z
    element.style.transform = `rotate(${object.rotation}deg)`
    element.addEventListener('pointerdown', event => this.beginDrag(event, object.localId))
    element.addEventListener('dblclick', event => this.beginTextEdit(event, object.localId))

    if (object.src) {
      const image = document.createElement('img')
      image.src = object.src
      image.alt = ''
      image.draggable = false
      element.appendChild(image)
    }
    if (object.type === 'bubble' || object.type === 'text') {
      const text = document.createElement('span')
      text.className = 'comic-object-label'
      text.textContent = object.text
      element.appendChild(text)
    }

    RESIZE_DIRECTIONS.forEach(direction => {
      const resize = document.createElement('button')
      resize.type = 'button'
      resize.className = 'comic-resize-handle'
      resize.dataset.direction = direction
      resize.title = `Resize ${direction.toUpperCase()}`
      resize.setAttribute('aria-label', resize.title)
      resize.addEventListener('pointerdown', event => this.beginResize(event, object.localId))
      element.appendChild(resize)
    })
    return element
  }

  selectObject (localId) {
    this.selectedId = localId
    this.canvasLayerTarget.querySelectorAll('.comic-canvas-object').forEach(element => {
      element.classList.toggle('selected', element.dataset.objectId === localId)
    })
    this.objectToolbarTarget.hidden = !localId
    const object = this.currentPage().objects.find(item => item.localId === localId)
    this.editTextButtonTarget.hidden = !object || !['bubble', 'text'].includes(object.type)
  }

  clearSelection (event) {
    if (event.target !== this.canvasLayerTarget) return
    this.selectObject(null)
  }

  selectedObject () {
    return this.currentPage().objects.find(object => object.localId === this.selectedId)
  }

  beginDrag (event, localId) {
    if (event.target.closest('.comic-resize-handle')) return
    event.preventDefault()
    event.stopPropagation()
    this.selectObject(localId)
    const object = this.selectedObject()
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    const start = { clientX: event.clientX, clientY: event.clientY, x: object.x, y: object.y }
    const move = pointer => {
      object.x = Math.max(0, Math.min(100 - object.width, start.x + (pointer.clientX - start.clientX) / rect.width * 100))
      object.y = Math.max(0, Math.min(100 - object.height, start.y + (pointer.clientY - start.clientY) / rect.height * 100))
      this.updateObjectStyle(object)
    }
    this.trackPointer(move)
  }

  beginResize (event, localId) {
    event.preventDefault()
    event.stopPropagation()
    this.selectObject(localId)
    const object = this.selectedObject()
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    const direction = event.currentTarget.dataset.direction || 'se'
    const start = {
      clientX: event.clientX,
      clientY: event.clientY,
      x: object.x,
      y: object.y,
      width: object.width,
      height: object.height
    }
    const move = pointer => {
      const deltaX = (pointer.clientX - start.clientX) / rect.width * 100
      const deltaY = (pointer.clientY - start.clientY) / rect.height * 100
      const right = start.x + start.width
      const bottom = start.y + start.height

      if (direction.includes('w')) {
        object.x = Math.max(0, Math.min(right - 6, start.x + deltaX))
        object.width = right - object.x
      } else if (direction.includes('e')) {
        object.x = start.x
        object.width = Math.max(6, Math.min(100 - start.x, start.width + deltaX))
      }

      if (direction.includes('n')) {
        object.y = Math.max(0, Math.min(bottom - 6, start.y + deltaY))
        object.height = bottom - object.y
      } else if (direction.includes('s')) {
        object.y = start.y
        object.height = Math.max(6, Math.min(100 - start.y, start.height + deltaY))
      }
      this.updateObjectStyle(object)
    }
    this.trackPointer(move)
  }

  trackPointer (move) {
    const finish = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', finish)
      document.removeEventListener('pointercancel', finish)
      this.abortPointerInteraction = null
      this.queueSave()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', finish, { once: true })
    document.addEventListener('pointercancel', finish, { once: true })
    this.abortPointerInteraction = finish
  }

  updateObjectStyle (object) {
    const element = this.canvasLayerTarget.querySelector(`[data-object-id="${globalThis.CSS.escape(object.localId)}"]`)
    if (!element) return
    element.style.left = `${object.x}%`
    element.style.top = `${object.y}%`
    element.style.width = `${object.width}%`
    element.style.height = `${object.height}%`
    element.style.zIndex = object.z
    element.style.transform = `rotate(${object.rotation}deg)`
  }

  beginTextEdit (event, localId) {
    const object = this.currentPage().objects.find(item => item.localId === localId)
    if (!object || !['bubble', 'text'].includes(object.type)) return
    event.preventDefault()
    event.stopPropagation()
    this.editObjectText(object, event.currentTarget)
  }

  editSelectedText () {
    const object = this.selectedObject()
    if (!object || !['bubble', 'text'].includes(object.type)) return this.setStatus('Select a text or speech shape to edit.', true)
    this.editObjectText(object)
  }

  editObjectText (object, element = null) {
    const value = globalThis.prompt('Text', object.text)
    if (value === null) return
    object.text = value.slice(0, 500)
    element ||= this.canvasLayerTarget.querySelector(`[data-object-id="${globalThis.CSS.escape(object.localId)}"]`)
    const text = element?.querySelector('.comic-object-label')
    if (text) text.textContent = object.text
    this.queueSave()
  }

  rotateLeft () { this.rotateSelected(-15) }
  rotateRight () { this.rotateSelected(15) }

  rotateSelected (amount) {
    const object = this.selectedObject()
    if (!object) return
    object.rotation = (object.rotation + amount) % 360
    this.updateObjectStyle(object)
    this.queueSave()
  }

  deleteSelected () {
    if (!this.selectedId) return
    const page = this.currentPage()
    page.objects = page.objects.filter(object => object.localId !== this.selectedId)
    this.normalizeLayers(page)
    this.renderPage()
    this.queueSave()
  }

  handleKeydown (event) {
    if (!this.selectedId || !['Delete', 'Backspace'].includes(event.key)) return
    const target = event.target
    if (target instanceof globalThis.Element && target.closest('input, textarea, select, [contenteditable="true"]')) return
    event.preventDefault()
    this.deleteSelected()
  }

  duplicateSelected () {
    const object = this.selectedObject()
    if (!object) return
    this.addObject({ ...object, localId: this.localId(), x: Math.min(object.x + 4, 100 - object.width), y: Math.min(object.y + 4, 100 - object.height) })
  }

  bringForward () { this.moveLayer(1) }
  sendBackward () { this.moveLayer(-1) }

  moveLayer (direction) {
    const page = this.currentPage()
    const sorted = [...page.objects].sort((a, b) => a.z - b.z)
    const index = sorted.findIndex(object => object.localId === this.selectedId)
    const target = index + direction
    if (index < 0 || target < 0 || target >= sorted.length) return
    ;[sorted[index], sorted[target]] = [sorted[target], sorted[index]]
    sorted.forEach((object, position) => { object.z = position + 1 })
    this.renderPage()
    this.selectObject(sorted[target].localId)
    this.queueSave()
  }

  normalizeLayers (page) {
    page.objects.sort((a, b) => a.z - b.z).forEach((object, index) => { object.z = index + 1 })
  }

  addPage () {
    if (this.pages.length >= MAX_PAGES) return this.setStatus(`A comic can contain up to ${MAX_PAGES} pages.`, true)
    this.pages.push(this.blankPage())
    this.pageIndex = this.pages.length - 1
    this.stage = 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
    this.queueSave()
  }

  deletePage () {
    if (this.pages.length <= 1) return this.setStatus('A comic must contain at least one page.', true)
    if (!globalThis.confirm(`Delete page ${this.pageIndex + 1}?`)) return

    this.pages.splice(this.pageIndex, 1)
    this.pageIndex = Math.min(this.pageIndex, this.pages.length - 1)
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
    this.queueSave()
  }

  switchPage (event) {
    this.pageIndex = Number(event.currentTarget.dataset.pageIndex)
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
  }

  renderPageButtons () {
    this.pageButtonsTarget.innerHTML = ''

    this.pages.forEach((page, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = index + 1
      button.dataset.pageIndex = index
      button.classList.add('btn', 'w-35px', 'h-35px', 'bg-comic-strip', 'rounded-circle', 'p-0', 'fw-bold', 'border-0', 'text-white', 'scale-hover')

      if (index === this.pageIndex) {
        button.classList.add('active', 'bg-comic-strip-active')
      }

      button.addEventListener('click', event => this.switchPage(event))
      this.pageButtonsTarget.appendChild(button)
    })
    const atPageLimit = this.pages.length >= MAX_PAGES
    this.addPageButtonTarget.disabled = atPageLimit
    this.addPageButtonTarget.title = atPageLimit ? `Maximum ${MAX_PAGES} pages reached` : `Add page (${this.pages.length}/${MAX_PAGES})`
    this.deletePageButtonTarget.disabled = this.pages.length <= 1
    this.deletePageButtonTarget.title = this.pages.length <= 1 ? 'A comic must contain one page' : `Delete page ${this.pageIndex + 1}`
  }

  filterAssets (event) {
    const query = event.currentTarget.value.toLowerCase().trim()
    this.assetListTarget.querySelectorAll('.comic-saved-strip, .comic-saved-asset').forEach(asset => {
      asset.hidden = Boolean(query) && !asset.dataset.search.includes(query)
    })
  }

  queueSave () {
    this.hasUnsavedChanges = true
    this.setStatus('Unsaved changes — click Done to save')
  }

  async saveProject () {
    if (!this.pages.some(page => page.backgroundKey)) {
      this.setStatus('Add a background to at least one page before saving.', true)
      return false
    }
    const removedPages = this.removeBackgroundlessPages()
    if (!this.hasUnsavedChanges) return true
    this.setStatus('Saving…')
    try {
      const response = await fetch(this.updateUrl, {
        method: 'PATCH',
        headers: this.jsonHeaders(),
        body: JSON.stringify({ comic_project: this.serializedProject() })
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.errors?.join(', ') || 'Comic could not be saved.')
      result.pages?.forEach((page, index) => { if (this.pages[index]) this.pages[index].id = page.id })
      this.hasUnsavedChanges = false
      this.setStatus(removedPages > 0
        ? `${removedPages} page${removedPages === 1 ? '' : 's'} without a background removed. All changes saved.`
        : 'All changes saved')
      return true
    } catch (error) {
      this.setStatus(error.message, true)
      return false
    }
  }

  removeBackgroundlessPages () {
    const currentPage = this.currentPage()
    const pagesWithBackgrounds = this.pages.filter(page => page.backgroundKey)
    const removedPages = this.pages.length - pagesWithBackgrounds.length
    if (removedPages === 0 || pagesWithBackgrounds.length === 0) return removedPages

    this.pages = pagesWithBackgrounds
    const currentPageIndex = this.pages.indexOf(currentPage)
    this.pageIndex = currentPageIndex >= 0 ? currentPageIndex : Math.min(this.pageIndex, this.pages.length - 1)
    this.hasUnsavedChanges = true
    this.renderPageButtons()
    return removedPages
  }

  warnBeforeUnload (event) {
    if (!this.hasUnsavedChanges) return
    event.preventDefault()
    event.returnValue = ''
  }

  warnBeforeVisit (event) {
    if (!this.hasUnsavedChanges || globalThis.confirm('You have unsaved comic changes. Leave without saving?')) return
    event.preventDefault()
  }

  serializedProject () {
    return {
      name: this.projectNameTarget.value.trim(),
      pages: this.pages.map(page => ({
        id: page.id,
        background_key: page.backgroundKey,
        editor_data: {
          version: 1,
          objects: page.objects.map(object => ({
            type: object.type,
            asset_id: object.assetId,
            source_attachment_id: object.sourceAttachmentId,
            src: object.src,
            text: object.text,
            x: object.x,
            y: object.y,
            width: object.width,
            height: object.height,
            rotation: object.rotation,
            z: object.z
          }))
        }
      }))
    }
  }

  jsonHeaders () {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
    }
  }

  async renderPreview () {
    try {
      await this.renderPageToCanvas(this.currentPage(), this.previewCanvasTarget)
    } catch (error) {
      this.setStatus(error.message || 'Preview could not be rendered.', true)
    }
  }

  async renderPageToCanvas (page, canvas) {
    canvas.width = 900
    canvas.height = 900
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, 900, 900)
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, 900, 900)

    const background = this.backgroundSources.get(page.backgroundKey)
    if (background) context.drawImage(await this.loadImage(background), 0, 0, 900, 900)

    for (const object of [...page.objects].sort((a, b) => a.z - b.z)) {
      context.save()
      const x = object.x / 100 * 900
      const y = object.y / 100 * 900
      const width = object.width / 100 * 900
      const height = object.height / 100 * 900
      context.translate(x + width / 2, y + height / 2)
      context.rotate(object.rotation * Math.PI / 180)
      if (object.src) {
        const image = await this.loadImage(object.src)
        context.drawImage(image, -width / 2, -height / 2, width, height)
      }
      if (object.type === 'text' || object.type === 'bubble') {
        const maxWidth = object.type === 'bubble' ? width * 0.66 : width
        const fontSize = Math.max(16, Math.min(42, height * (object.type === 'bubble' ? 0.18 : 0.38)))
        context.fillStyle = '#111111'
        context.font = `700 ${fontSize}px "Bad Comic", "Comic Sans MS", sans-serif`
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        this.drawWrappedText(context, object.text, 0, 0, maxWidth, fontSize * 1.15)
      }
      context.restore()
    }
    return canvas
  }

  drawWrappedText (context, text, x, centerY, maxWidth, lineHeight) {
    const words = String(text || '').split(/\s+/)
    const lines = []
    let line = ''
    words.forEach(word => {
      const test = line ? `${line} ${word}` : word
      if (context.measureText(test).width > maxWidth && line) {
        lines.push(line)
        line = word
      } else line = test
    })
    if (line) lines.push(line)
    const startY = centerY - (lines.length - 1) * lineHeight / 2
    lines.slice(0, 6).forEach((value, index) => context.fillText(value, x, startY + index * lineHeight, maxWidth))
  }

  loadImage (source) {
    return new Promise((resolve, reject) => {
      const image = new globalThis.Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('A comic image could not be loaded.'))
      image.src = source
    })
  }

  canvasBlob (canvas) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('A comic page could not be rendered.')), 'image/png', 1)
      } catch (error) {
        const message = error.name === 'SecurityError'
          ? 'One of the comic images cannot be exported securely. Remove it and add it again.'
          : 'A comic page could not be rendered.'
        reject(new Error(message))
      }
    })
  }

  async exportToLibrary () {
    const rackId = this.rackSelectTarget.value
    if (!rackId) return this.setStatus('Select a Library rack.', true)

    this.exportButtonTarget.disabled = true
    this.setStatus('Rendering all pages…')
    try {
      if (!await this.saveProject()) return
      const formData = new FormData()
      formData.append('library_rack_id', rackId)
      for (let index = 0; index < this.pages.length; index += 1) {
        const canvas = document.createElement('canvas')
        await this.renderPageToCanvas(this.pages[index], canvas)
        const blob = await this.canvasBlob(canvas)
        formData.append('pages[]', blob, `comic-page-${index + 1}.png`)
      }
      const response = await fetch(this.exportUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
        body: formData
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.errors?.join(', ') || 'Comic could not be added to the Library.')
      this.setStatus(result.message || 'Comic saved to your Library.')
      globalThis.bootstrap?.Modal.getInstance(document.getElementById('comicLibraryModal'))?.hide()
      if (result.library_url && globalThis.confirm('Comic saved. Open the Library rack now?')) globalThis.location.href = result.library_url
    } catch (error) {
      this.setStatus(error.message, true)
    } finally {
      this.exportButtonTarget.disabled = false
    }
  }

  setStatus (message, error = false) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle('error', error)
  }
}
